// The shell's tour host: the host's Tutorial button (the `startEvent` window event) opens the chooser —
// the host's built-in tutorials, then the open app's guides — and any page's launchTour() (launch.ts)
// runs its tour at once. One Spotlight at a time; the host mounts it in its layout (inside
// <GuidesHostProvider>) so it survives the route changes a tour makes.
import { CheckCircle2, ChevronRight, GraduationCap, Loader2, X } from 'lucide-react';
import * as React from 'react';
import { useGuidesHost } from '../host.tsx';
import { onTourLaunch, tourFromGuide } from './launch.ts';
import { isTourSeen, Spotlight, type SpotlightTour } from './Spotlight.tsx';

/** A tour the host offers besides the app's guides (e.g. a product tour of its own UI). */
export interface TourListing {
  tour: SpotlightTour;
  description: string;
}

/** The host's own tutorials: a group of the chooser before "This project". */
export interface BuiltInTours {
  /** The group's key (its test id is `tour-group-<key>`). */
  key: string;
  label: string;
  /** What the group says when it has no tour (e.g. no app open). */
  empty: string;
  /** The host's tours now (it knows what is open). */
  tours(): TourListing[];
}

type Pick = { key: string; title: string; description: string; steps: number | null; run(): Promise<void> };

function Chooser({
  groups,
  onClose,
}: {
  groups: { key: string; label: string; picks: Pick[] | null; empty: string }[];
  onClose: () => void;
}) {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center" data-testid="tour-chooser">
      <div className="absolute inset-0 bg-[rgba(9,14,34,.55)]" onClick={onClose} />
      <div
        role="dialog"
        aria-label="Tutorials"
        className="relative w-[460px] max-w-[calc(100vw-24px)] rounded-[10px] bg-card-bg text-page-text shadow-2xl border border-page-divider overflow-hidden"
      >
        <div className="flex items-center justify-between px-4 py-3 bg-nav-active text-white">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <GraduationCap size={16} /> Tutorials
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="tour-chooser-close"
            className="p-1 rounded hover:bg-white/20 border-0 bg-transparent text-white cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-3 flex flex-col gap-3 max-h-[60vh] overflow-y-auto">
          {groups.map((g) => (
            <div key={g.key} data-testid={`tour-group-${g.key}`}>
              <div className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-page-muted">
                {g.label}
              </div>
              {g.picks === null ? (
                <div className="px-1 text-xs text-page-muted flex items-center gap-1.5">
                  <Loader2 size={12} className="animate-spin" /> Loading…
                </div>
              ) : g.picks.length === 0 ? (
                <div className="px-1 text-xs text-page-muted">{g.empty}</div>
              ) : (
                <div className="flex flex-col gap-2">
                  {g.picks.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      data-testid={`tour-pick-${t.key}`}
                      onClick={() => void t.run()}
                      className="w-full text-left rounded-lg border border-page-divider hover:border-nav-active hover:bg-page-bg px-4 py-3 cursor-pointer bg-transparent transition-colors group text-page-text"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{t.title}</span>
                        {isTourSeen(t.key) && (
                          <span className="inline-flex items-center gap-1 text-[10px] text-green-600">
                            <CheckCircle2 size={11} /> seen
                          </span>
                        )}
                        <ChevronRight
                          size={15}
                          className="ml-auto text-page-muted group-hover:text-nav-active"
                        />
                      </div>
                      {t.description && <div className="text-xs text-page-muted mt-1">{t.description}</div>}
                      {t.steps !== null && (
                        <div className="text-[10px] text-page-muted mt-1">{t.steps} steps</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function TourHost({
  startEvent,
  builtIn,
}: {
  /** The window event that opens the chooser (the host's Tutorial button, the speed dial's Guides). */
  startEvent: string;
  /** The host's own tutorials, if any. */
  builtIn?: BuiltInTours;
}) {
  const host = useGuidesHost();
  const api = host.client;
  const [tour, setTour] = React.useState<SpotlightTour | null>(null);
  const [choosing, setChoosing] = React.useState(false);
  const [guides, setGuides] = React.useState<Pick[] | null>(null);
  const place = React.useMemo(
    () => ({ scope: host.scope, ...(host.routeOf ? { routeOf: host.routeOf } : {}) }),
    [host.scope, host.routeOf],
  );

  React.useEffect(() => onTourLaunch((t) => (setChoosing(false), setTour(t))), []);
  React.useEffect(() => {
    const open = () => setChoosing(true);
    window.addEventListener(startEvent, open);
    return () => window.removeEventListener(startEvent, open);
  }, [startEvent]);

  // The open app's guides, each launchable as a tour (read when the chooser opens).
  React.useEffect(() => {
    if (!choosing) return;
    if (!api) return setGuides([]);
    let alive = true;
    setGuides(null);
    api
      .GET('/guides')
      .then(({ data }) => {
        if (!alive) return;
        setGuides(
          (data?.guides ?? [])
            .filter((g) => g.sections > 0)
            .map((g) => ({
              key: `guide:${place.scope}/${g.key}`,
              title: g.title,
              description: g.description ?? '',
              steps: g.sections,
              run: async () => {
                const { data: full } = await api.GET('/guides/{key}', { params: { path: { key: g.key } } });
                if (!full?.sectionList.length) return;
                setChoosing(false);
                setTour(tourFromGuide(full, place));
              },
            })),
        );
      })
      .catch(() => alive && setGuides([]));
    return () => {
      alive = false;
    };
  }, [choosing, api, place]);

  const own = builtIn ? builtIn.tours() : [];
  return (
    <>
      {choosing && (
        <Chooser
          onClose={() => setChoosing(false)}
          groups={[
            // The host's own tutorials (a product tour of its UI); a host without any offers only the app's guides.
            ...(builtIn
              ? [
                  {
                    key: builtIn.key,
                    label: builtIn.label,
                    empty: builtIn.empty,
                    picks: own.map((t) => ({
                      key: t.tour.id,
                      title: t.tour.title,
                      description: t.description,
                      steps: t.tour.steps.length,
                      run: async () => {
                        setChoosing(false);
                        setTour(t.tour);
                      },
                    })),
                  },
                ]
              : []),
            {
              key: 'project',
              label: 'This project',
              empty: 'This project has no guides yet.',
              picks: guides,
            },
          ]}
        />
      )}
      {tour && <Spotlight key={tour.id} tour={tour} navigate={host.navigate} onClose={() => setTour(null)} />}
    </>
  );
}
